// ── Info Screens ───────────────────────────────────────────────────────────────
// One designed slide (the TEMPLATE) drives many physical door screens. The
// template's text layers are bound to named fields; each sign supplies its own
// values, gets rendered on its own, and is pushed to its own screen.
//
// The template itself is drawn in the ordinary Slide Builder — this page never
// touches geometry. It owns three things the builder does not:
//   · the field schema        (what a door has: doctor name, room, status…)
//   · the bindings            (which text layer shows which field)
//   · the signs               (the doors themselves, their values and screens)
//
// Publishing is the same path the menu board uses: render in the browser with
// applySignBindings → /info-screens/:id/save-jpeg → /info-screens/:id/publish-html.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SendToTVModal from '../../components/SendToTVModal';
import BrandNav from '../../components/common/BrandNav';
import {
  SlidePreview, renderSlideToCanvas, parseZones, dimFor,
  applySignBindings, signFieldDefaults, collectBindings,
} from '../../utils/slideRenderer';

const IcoHamburger = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
    <rect x="2" y="4"  width="16" height="2" rx="1"/>
    <rect x="2" y="9"  width="16" height="2" rx="1"/>
    <rect x="2" y="14" width="16" height="2" rx="1"/>
  </svg>
);

function pageMenuItemStyle(danger = false, disabled = false) {
  return {
    display: 'flex', alignItems: 'center', gap: 9,
    width: '100%', padding: '9px 14px', border: 'none',
    background: 'transparent', cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, color: disabled ? '#bbb' : (danger ? '#C62828' : '#333'),
    textAlign: 'left', opacity: disabled ? 0.5 : 1,
  };
}


const THUMB_W = 150, THUMB_H = 168;   // the box a catalogue card's preview sits in
const BIG_W = 520, BIG_H = 620;       // and the box the full-size preview sits in
const chip = { background: '#F1F5F9', color: '#475569', borderRadius: 20,
               padding: '3px 10px', fontWeight: 600, whiteSpace: 'nowrap' };

// The shared library, grouped the way a customer thinks about their building.
const SECTORS = [
  { k: 'healthcare',  l: 'Healthcare',        l_ar: 'الرعاية الصحية' },
  { k: 'hospitality', l: 'Hotels',            l_ar: 'الفنادق والضيافة' },
  { k: 'corporate',   l: 'Offices',           l_ar: 'الشركات والمكاتب' },
  { k: 'education',   l: 'Education',         l_ar: 'التعليم' },
  { k: 'industrial',  l: 'Industrial',        l_ar: 'المصانع والمخازن' },
  { k: 'retail',      l: 'Retail & F&B',      l_ar: 'المطاعم والتجزئة' },
  { k: 'government',  l: 'Government & Banks',l_ar: 'الحكومي والبنوك' },
  { k: 'fitness',     l: 'Gyms & Studios',    l_ar: 'النوادي والصالات' },
  { k: 'universal',   l: 'Works anywhere',    l_ar: 'يشتغل في أي مكان' },
];

const FIELD_TYPES = [
  { v: 'text',   l: 'Text',   l_ar: 'نص' },
  { v: 'image',  l: 'Image',  l_ar: 'صورة' },
  // The value is the text to encode; the server renders the picture.
  { v: 'qr',     l: 'QR code', l_ar: 'كود QR' },
  { v: 'status', l: 'Status', l_ar: 'حالة' },
  // Clock and date read the screen's own clock — no value to fill in, no
  // network needed, and they keep running through an outage. Only meaningful
  // on a published page; a baked image would freeze them.
  { v: 'clock',  l: 'Clock',  l_ar: 'ساعة', live: true },
  { v: 'date',   l: 'Date',   l_ar: 'تاريخ', live: true },
  // A schedule is one field, not eight. Rows live in the value, so a screen can
  // show three entries today and six tomorrow without touching the design.
  { v: 'list',   l: 'List',   l_ar: 'قايمة' },
  // Computed from coordinates, never fetched — it keeps working with no
  // network, which is the whole reason clock and date are here too.
  { v: 'prayer', l: 'Prayer times', l_ar: 'مواقيت الصلاة', live: true },
  // Ticks like a clock but needs a date to aim at, so it is not in LIVE_TYPES.
  { v: 'countdown', l: 'Countdown', l_ar: 'عدّاد' },
];
const LIVE_TYPES = ['clock', 'date', 'prayer'];

// A few cities so nobody has to look up coordinates to get started.
// Each place carries its TIME ZONE, not an offset: Egypt and Jordan change
// their clocks, and a number would be an hour wrong for half the year.
const PRAYER_PLACES = [
  { l: 'Cairo',     l_ar: 'القاهرة',    lat: 30.0444, lng: 31.2357, tzname: 'Africa/Cairo',    method: 'EGYPT' },
  { l: 'Alexandria',l_ar: 'الإسكندرية', lat: 31.2001, lng: 29.9187, tzname: 'Africa/Cairo',    method: 'EGYPT' },
  { l: 'Giza',      l_ar: 'الجيزة',     lat: 30.0131, lng: 31.2089, tzname: 'Africa/Cairo',    method: 'EGYPT' },
  { l: 'Riyadh',    l_ar: 'الرياض',     lat: 24.7136, lng: 46.6753, tzname: 'Asia/Riyadh',     method: 'UMM_AL_QURA' },
  { l: 'Makkah',    l_ar: 'مكة',        lat: 21.3891, lng: 39.8579, tzname: 'Asia/Riyadh',     method: 'UMM_AL_QURA' },
  { l: 'Dubai',     l_ar: 'دبي',        lat: 25.2048, lng: 55.2708, tzname: 'Asia/Dubai',      method: 'DUBAI' },
  { l: 'Doha',      l_ar: 'الدوحة',     lat: 25.2854, lng: 51.5310, tzname: 'Asia/Qatar',      method: 'QATAR' },
  { l: 'Kuwait',    l_ar: 'الكويت',     lat: 29.3759, lng: 47.9774, tzname: 'Asia/Kuwait',     method: 'KUWAIT' },
  { l: 'Amman',     l_ar: 'عمّان',      lat: 31.9454, lng: 35.9284, tzname: 'Asia/Amman',      method: 'MWL' },
  { l: 'Istanbul',  l_ar: 'إسطنبول',    lat: 41.0082, lng: 28.9784, tzname: 'Europe/Istanbul', method: 'MWL' },
];
const PRAYER_DEFAULT = { lat: 30.0444, lng: 31.2357, tzname: 'Africa/Cairo',
                         method: 'EGYPT', madhab: 'standard', clock: '24' };

const DEFAULT_COLUMNS = [
  { key: 'a', label: 'Time',  label_ar: 'الوقت',  width: 34, size: 15, weight: 'bold',   color: '#1D4ED8', align: 'start' },
  { key: 'b', label: 'Entry', label_ar: 'البند',  width: 66, size: 15, weight: 'normal', color: '#12213A', align: 'start' },
];
const DEFAULT_ROWS = [
  { a: '09:00 - 10:30', b: 'First item' },
  { a: '11:00 - 12:00', b: 'Second item' },
];

const DEFAULT_STATUS_OPTIONS = [
  { value: 'available', label: 'Available', label_ar: 'متاح',   color: '#16A34A' },
  { value: 'busy',      label: 'Busy',      label_ar: 'مشغول',  color: '#DC2626' },
  { value: 'away',      label: 'Away',      label_ar: 'بالخارج', color: '#D97706' },
];

// Field keys become {{mustache}} tokens, so keep them to what a token can hold.
const slugKey = (s) => (s || '')
  .toLowerCase().trim()
  .replace(/[^a-z0-9_]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

const uniqueKey = (base, taken) => {
  let k = base || 'field';
  let n = 2;
  while (taken.includes(k)) k = `${base}_${n++}`;
  return k;
};

const btn = (extra = {}) => ({
  padding: '6px 12px', borderRadius: 6, border: '1.5px solid #D0D7DE',
  background: '#fff', color: '#333', fontSize: 12, fontWeight: 600,
  cursor: 'pointer', ...extra,
});
const primaryBtn = btn({ background: '#1565C0', borderColor: '#1565C0', color: '#fff' });
const input = {
  height: 30, border: '1.5px solid #E0E0E0', borderRadius: 6, padding: '0 8px',
  fontSize: 12, boxSizing: 'border-box', width: '100%',
};

export default function InfoScreens() {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [menuData,   setMenuData]   = useState({ categories: [], items: {}, allItems: [] });
  const [templates,  setTemplates]  = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [signs,      setSigns]      = useState([]);
  const [selSignId,  setSelSignId]  = useState(null);
  const [tab,        setTab]        = useState('screens'); // 'templates' | 'screens'
  const [showPageMenu, setShowPageMenu] = useState(false);
  const pageMenuRef = useRef(null);
  // English is the default on every entry and the app stays LTR until the
  // user presses the language button — the choice is deliberately not persisted.
  const [lang,       setLang]       = useState('en');
  // The screen row packs a thumbnail, a name, a target picker and five buttons
  // onto one line. That does not fit a phone: the name column got squeezed to a
  // few pixels and wrapped one character per line while the buttons ran off the
  // right edge. Below this width the row breaks into two.
  const [colsFor,    setColsFor]    = useState(null);   // which list's columns are open
  const [prayFor,    setPrayFor]    = useState(null);
  const [prayMethods, setPrayMethods] = useState([]);
  const [prayPreview, setPrayPreview] = useState({});
  const [showLib,    setShowLib]    = useState(false);
  const [libItems,   setLibItems]   = useState([]);
  const [libSector,  setLibSector]  = useState('healthcare');
  const [libPick,    setLibPick]    = useState(null);
  const [libOpen,    setLibOpen]    = useState(null);   // template shown full size
  const [isNarrow,   setIsNarrow]   = useState(() => window.innerWidth <= 640);
  useEffect(() => {
    const h = () => setIsNarrow(window.innerWidth <= 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [busy,       setBusy]       = useState('');
  const [sendFor,    setSendFor]    = useState(null);      // sign ids for the TV modal
  const [devices,    setDevices]    = useState([]);        // screens this brand owns
  const [groups,     setGroups]     = useState([]);        // and its screen groups
  // Bumping a sign's entry re-requests its live screenshot. The card shows what
  // the door is ACTUALLY displaying, not a render of what we hope it shows.
  const [shotVer,    setShotVer]    = useState({});
  const bumpShot = (id) => setShotVer(v => ({ ...v, [id]: Date.now() }));
  const photoInputRef = useRef(null);
  // Close the page menu on any outside click, like the Slides page does.
  useEffect(() => {
    const h = (e) => { if (pageMenuRef.current && !pageMenuRef.current.contains(e.target)) setShowPageMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const pendingPhotoRef = useRef(null);                    // { signId, fieldKey }

  const template = templates.find(t => t.id === templateId) || null;
  const fields   = useMemo(() => parseZones(template?.sign_fields), [template]);
  const selSign  = signs.find(s => s.id === selSignId) || null;
  const isAr     = lang === 'ar';
  // An entry is assigned when it points at a device OR a group.
  const hasTarget = (x) => !!(x?.device_name || x?.group_id);
  const groupOf   = (x) => groups.find(g => g.id === x?.group_id);
  const targetLabel = (x) => {
    const g = groupOf(x);
    if (g) return `${g.name} · ${(g.device_names || []).length}`;
    return x?.device_name || '';
  };
  const L        = (en, ar) => (isAr ? ar : en);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadTemplates = async () => {
    const r = await api.get(`/brand-slides?brand_id=${brandId}&kind=doorsign`);
    setTemplates(r.data || []);
    return r.data || [];
  };
  const loadSigns = async (tid) => {
    if (!tid) { setSigns([]); return []; }
    const r = await api.get(`/info-screens?brand_id=${brandId}&template_id=${tid}`);
    setSigns(r.data || []);
    return r.data || [];
  };

  useEffect(() => {
    (async () => {
      try {
        const [rRes, mdRes, tpls, dRes, gRes] = await Promise.all([
          api.get(`/brands/${brandId}`),
          api.get(`/menuboard/menu-data/${brandId}`).catch(() => ({ data: { categories: [], items: {}, allItems: [] } })),
          loadTemplates(),
          api.get(`/users/my-devices?brand_id=${brandId}`).catch(() => ({ data: [] })),
          api.get(`/users/my-groups?brand_id=${brandId}`).catch(() => ({ data: [] })),
        ]);
        // The conventions come from the server so the editor and the door can
        // never disagree about what EGYPT or UMM_AL_QURA mean.
        api.get('/info-screens/meta/prayer-methods')
          .then(r => setPrayMethods(r.data || [])).catch(() => {});
        setRestaurant(rRes.data);
        setMenuData(mdRes.data);
        setDevices(dRes.data || []);
        setGroups(gRes.data || []);
        if (tpls.length) { setTemplateId(tpls[0].id); await loadSigns(tpls[0].id); }
      } catch (e) {
        toast.error('Failed to load info screens');
      } finally { setLoading(false); }
    })();
  }, [brandId]); // eslint-disable-line

  const switchTemplate = async (tid) => {
    setTemplateId(tid);
    setSelSignId(null);
    const list = await loadSigns(tid);
    if (list.length) setSelSignId(list[0].id);
  };

  // ── The shared library ──────────────────────────────────────────────────────
  // Browsing is free; taking one copies it into this brand, and from that moment
  // it is an ordinary template of theirs. Nothing they do can reach the library.
  const openLibrary = async () => {
    setShowLib(true);
    if (libItems.length) return;
    try {
      const r = await api.get('/brand-slides/library');
      setLibItems(r.data || []);
      const first = (r.data || [])[0];
      if (first?.library_sector) setLibSector(first.library_sector);
    } catch { toast.error(L('Could not open the library', 'مقدرتش أفتح المكتبة')); }
  };

  const takeLibraryTemplate = async (item) => {
    setBusy('lib');
    try {
      const r = await api.post(`/brand-slides/library/${item.id}/use`, {
        brand_id: parseInt(brandId, 10),
        slide_name: item.slide_name,
      });
      await loadTemplates();
      setTemplateId(r.data.id);
      setSigns([]);
      setShowLib(false);
      setLibPick(null);
      setLibOpen(null);
      toast.success(L('Copied — it is yours to edit now', 'اتنسخت — بقت بتاعتك تعدّل فيها'));
    } catch (e) { toast.error(e.response?.data?.error || 'Copy failed'); }
    setBusy('');
  };

  // ── Templates ───────────────────────────────────────────────────────────────
  const createTemplate = async () => {
    const name = window.prompt(L('Template name', 'اسم التمبلت'), L('Info Screen', 'شاشة معلومات'));
    if (!name) return;
    try {
      const r = await api.post('/brand-slides', {
        brand_id: parseInt(brandId, 10),
        slide_name: name,
        menu_kind: 'doorsign',
        // Doors are portrait far more often than not; still editable in the builder.
        orientation: 'V', width_px: 720, height_px: 1280,
        zone_mode: 'free', zones: [], text_layers: [],
      });
      const tpls = await loadTemplates();
      setTemplateId(r.data.id);
      setSigns([]);
      toast.success(L('Template created — design it next', 'اتعمل التمبلت — صمّمه دلوقتي'));
      if (tpls.length) navigate(`/brands/${brandId}/slide-builder/${r.data.id}?from=info-screens`);
    } catch (e) { toast.error(e.response?.data?.error || 'Create failed'); }
  };

  const saveSchema = async (nextFields, nextSlide) => {
    if (!template) return;
    const body = { sign_fields: nextFields ?? fields };
    if (nextSlide) {
      body.zones       = parseZones(nextSlide.zones);
      body.text_layers = parseZones(nextSlide.text_layers);
    }
    try {
      const r = await api.put(`/brand-slides/${template.id}/sign-schema`, body);
      setTemplates(ts => ts.map(t => (t.id === template.id ? r.data : t)));
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  // ── Field schema ────────────────────────────────────────────────────────────
  const addField = () => {
    const label = window.prompt(L('Field label', 'اسم الحقل'), '');
    if (!label) return;
    const key = uniqueKey(slugKey(label), fields.map(f => f.key));
    saveSchema([...fields, { key, label, label_ar: '', type: 'text', default: '' }]);
  };
  const patchField = (idx, patch) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    // Switching to status without options would leave an unusable dropdown.
    if (patch.type === 'status' && !next[idx].options?.length) {
      next[idx].options = DEFAULT_STATUS_OPTIONS;
      next[idx].default = next[idx].default || DEFAULT_STATUS_OPTIONS[0].value;
    }
    saveSchema(next);
  };
  const deleteField = (idx) => {
    const f = fields[idx];
    if (!window.confirm(L(`Delete field "${f.label || f.key}"? Layers bound to it fall back to their own text.`,
                          `تحذف حقل "${f.label || f.key}"؟ الطبقات المربوطة بيه هترجع لنصها الأصلي.`))) return;
    saveSchema(fields.filter((_, i) => i !== idx));
  };

  // ── Bindings ────────────────────────────────────────────────────────────────
  // Flatten every text layer in the template — slide-level and per-zone — into
  // one addressable list so binding is a single dropdown per layer.
  const layerRefs = useMemo(() => {
    if (!template) return [];
    const out = [];
    parseZones(template.text_layers).forEach((l, i) => {
      out.push({ scope: 'slide', zoneIdx: null, idx: i, layer: l });
    });
    parseZones(template.zones).forEach((z, zi) => {
      (Array.isArray(z.text_layers) ? z.text_layers : []).forEach((l, i) => {
        out.push({ scope: 'zone', zoneIdx: zi, idx: i, layer: l, zoneName: z.name || `Zone ${zi + 1}` });
      });
    });
    return out;
  }, [template]);

  const imageZones = useMemo(
    () => parseZones(template?.zones)
      .map((z, zi) => ({ z, zi }))
      .filter(({ z }) => z.bg_image || z.bg_image_binding),
    [template]
  );

  // Zones with a solid fill can bind that fill to a status colour — a state
  // badge that recolours with the state while its label stays white.
  const colorZones = useMemo(
    () => parseZones(template?.zones)
      .map((z, zi) => ({ z, zi }))
      .filter(({ z }) => z.bg_color || z.bg_color_binding),
    [template]
  );
  const statusFields = useMemo(() => fields.filter(f => f.type === 'status'), [fields]);

  // ── One list instead of two ───────────────────────────────────────────────
  // The old page asked you to declare abstract "fields", then separately bind
  // layers to them — two lists you had to join in your head. This walks what
  // the template actually DRAWS and asks one question per item: does this stay
  // the same on every screen, or does it change? Fields and bindings are still
  // what gets stored; they are just no longer what you have to think about.
  const elements = useMemo(() => {
    if (!template) return [];
    const out = [];
    const textOf = (l) => (l.text || l.text_ar || l.text_en || '').split('\n')[0];

    parseZones(template.text_layers).forEach((l, i) => {
      if (l.hidden) return;
      out.push({
        id: `s${i}`, kind: 'text', scope: 'slide', idx: i,
        tag: l.label || 't?', preview: textOf(l), fallback: '(empty text)',
        fieldKey: l.binding || null,
      });
    });
    parseZones(template.zones).forEach((z, zi) => {
      (Array.isArray(z.text_layers) ? z.text_layers : []).forEach((l, i) => {
        if (l.hidden) return;
        out.push({
          id: `z${zi}t${i}`, kind: 'text', scope: 'zone', zoneIdx: zi, idx: i,
          tag: l.label || 't?', preview: textOf(l), fallback: '(empty text)',
          fieldKey: l.binding || null,
        });
      });
      if (z.bg_image || z.bg_image_binding) {
        out.push({
          id: `z${zi}i`, kind: 'image', zoneIdx: zi, thumb: z.bg_image,
          preview: z.name || `Zone ${zi + 1}`, fallback: 'image',
          fieldKey: z.bg_image_binding || null,
        });
      }
      if (z.bg_color || z.bg_color_binding) {
        out.push({
          id: `z${zi}c`, kind: 'color', zoneIdx: zi, swatch: z.bg_color,
          preview: z.name || `Zone ${zi + 1}`, fallback: 'colour',
          fieldKey: z.bg_color_binding || null,
        });
      }
      // Only zones that already hold a list get their own row — otherwise every
      // template would grow one meaningless line per box. A plain zone is turned
      // into a list from its colour row's dropdown.
      if (z.list_binding) {
        out.push({
          id: `z${zi}l`, kind: 'list', zoneIdx: zi,
          preview: z.name || `Zone ${zi + 1}`, fallback: 'list',
          fieldKey: z.list_binding,
        });
      }
    });
    return out;
  }, [template]);

  const patchFieldByKey = (key, patch) =>
    saveSchema(fields.map(f => (f.key === key ? { ...f, ...patch } : f)));

  // The calculation lives on the server, so the editor asks it rather than
  // carrying a second copy that could drift from what the door will show.
  const loadPrayerPreview = async (f) => {
    try {
      const r = await api.get('/info-screens/meta/prayer-preview', {
        params: { lat: f.lat, lng: f.lng, tz: f.tz, tzname: f.tzname,
                  method: f.method, madhab: f.madhab, clock: f.clock },
      });
      // Only the readable form; the _24 twin is there for the page's own sorting.
      const today = Object.fromEntries(
        Object.entries(r.data.today || {}).filter(([k]) => !k.endsWith('_24')));
      setPrayPreview(pv => ({ ...pv, [f.key]: today }));
      toast.success(L(`Times are for UTC+${r.data.tz_used}`, `المواقيت على توقيت UTC+${r.data.tz_used}`));
    } catch { toast.error(L('Could not work those out', 'مقدرتش أحسبها')); }
  };

  const patchColumn = (f, ci, patch) => {
    const cols = (f.columns || []).map((c, i) => (i === ci ? { ...c, ...patch } : c));
    patchFieldByKey(f.key, { columns: cols });
  };
  const addColumn = (f) => {
    const cols = [...(f.columns || [])];
    const used = cols.map(c => c.key);
    let k = 'a'; while (used.includes(k)) k = String.fromCharCode(k.charCodeAt(0) + 1);
    patchFieldByKey(f.key, { columns: [...cols, { key: k, label: 'Column', label_ar: '', width: 30, size: 15, weight: 'normal', color: '#12213A', align: 'start' }] });
  };
  const removeColumn = (f, ci) =>
    patchFieldByKey(f.key, { columns: (f.columns || []).filter((_, i) => i !== ci) });

  // Flip one element between fixed and filled-per-screen. Creates or retires the
  // field behind it and rewrites the binding in the same save, so the two can
  // never drift apart.
  const setElementMode = (el, mode) => {
    if (!template) return;
    const slideLayers = parseZones(template.text_layers);
    const zones       = parseZones(template.zones);
    let nextFields    = [...fields];

    const setBinding = (key) => {
      if (el.kind === 'image')      zones[el.zoneIdx] = { ...zones[el.zoneIdx], bg_image_binding: key || undefined };
      else if (el.kind === 'list')  zones[el.zoneIdx] = { ...zones[el.zoneIdx], list_binding: key || undefined };
      else if (el.kind === 'color') zones[el.zoneIdx] = { ...zones[el.zoneIdx], bg_color_binding: key || undefined };
      else if (el.scope === 'slide') slideLayers[el.idx] = { ...slideLayers[el.idx], binding: key || undefined };
      else {
        const zl = [...(zones[el.zoneIdx].text_layers || [])];
        zl[el.idx] = { ...zl[el.idx], binding: key || undefined };
        zones[el.zoneIdx] = { ...zones[el.zoneIdx], text_layers: zl };
      }
    };

    if (mode === 'fixed') {
      const key = el.fieldKey;
      setBinding(null);
      // Retire the field only when nothing else was using it.
      if (key && elements.filter(e => e.fieldKey === key).length <= 1) {
        nextFields = nextFields.filter(f => f.key !== key);
      }
      saveSchema(nextFields, { zones, text_layers: slideLayers });
      return;
    }

    // Turning a colour box into a list moves the binding to a different property,
    // so the colour binding has to let go of it first.
    if (mode === 'list' && el.kind === 'color') {
      zones[el.zoneIdx] = { ...zones[el.zoneIdx], bg_color_binding: undefined };
      el = { ...el, kind: 'list', fieldKey: zones[el.zoneIdx].list_binding || null };
    }
    const type = ['status', 'clock', 'date', 'list', 'qr', 'prayer', 'countdown'].includes(mode) ? mode : 'text';
    let key = el.fieldKey;
    if (key) {
      nextFields = nextFields.map(f => f.key === key
        ? { ...f, type,
            ...(type === 'status' && !(f.options || []).length
              ? { options: DEFAULT_STATUS_OPTIONS, default: DEFAULT_STATUS_OPTIONS[0].value } : {}),
            ...(type === 'list' && !(f.columns || []).length
              ? { columns: DEFAULT_COLUMNS, default: DEFAULT_ROWS, max_rows: 6 } : {}),
            ...(type === 'prayer' && !Number.isFinite(Number(f.lat)) ? PRAYER_DEFAULT : {}),
            ...(type === 'countdown' && !f.mode ? { mode: 'until', format: 'auto', expired: '' } : {}) }
        : f);
    } else {
      // Name it after what it already says, so the list reads sensibly straight
      // away instead of showing "field_1".
      const seed  = (el.kind === 'text' ? el.preview : el.preview) || el.kind;
      const label = String(seed).slice(0, 28) || el.kind;
      key = uniqueKey(slugKey(label) || el.kind, nextFields.map(f => f.key));
      nextFields = [...nextFields, {
        key, label, label_ar: '', type,
        default: type === 'text' ? (el.kind === 'text' ? el.preview : '') : '',
        ...(type === 'status' ? { options: DEFAULT_STATUS_OPTIONS, default: DEFAULT_STATUS_OPTIONS[0].value } : {}),
        ...(type === 'list'   ? { columns: DEFAULT_COLUMNS, default: DEFAULT_ROWS, max_rows: 6 } : {}),
        ...(type === 'prayer' ? PRAYER_DEFAULT : {}),
        ...(type === 'countdown' ? { mode: 'until', format: 'auto', expired: '' } : {}),
      }];
    }
    setBinding(key);
    saveSchema(nextFields, { zones, text_layers: slideLayers });
  };

  const bindLayerTo = (ref, key) => {
    if (!template) return;
    const slideLayers = parseZones(template.text_layers);
    const zones       = parseZones(template.zones);
    if (ref.scope === 'slide') {
      slideLayers[ref.idx] = { ...slideLayers[ref.idx], binding: key || undefined };
    } else {
      const zl = [...(zones[ref.zoneIdx].text_layers || [])];
      zl[ref.idx] = { ...zl[ref.idx], binding: key || undefined };
      zones[ref.zoneIdx] = { ...zones[ref.zoneIdx], text_layers: zl };
    }
    saveSchema(fields, { zones, text_layers: slideLayers });
  };

  // Rows for one screen. Falls back to the field's sample rows so a new screen
  // shows something to edit instead of an empty box.
  const rowsOf = (sign, f) => {
    const v = (sign.field_values || {})[f.key];
    if (Array.isArray(v)) return v;
    return Array.isArray(f.default) ? f.default : [];
  };
  const setRows = (sign, f, rows) => setSignValue(sign, f.key, rows);
  const patchRow = (sign, f, ri, patch) =>
    setRows(sign, f, rowsOf(sign, f).map((r, i) => (i === ri ? { ...r, ...patch } : r)));
  const addRow = (sign, f) =>
    setRows(sign, f, [...rowsOf(sign, f), Object.fromEntries((f.columns || []).map(c => [c.key, '']))]);
  const removeRow = (sign, f, ri) =>
    setRows(sign, f, rowsOf(sign, f).filter((_, i) => i !== ri));

  const bindZoneProp = (zi, prop, key) => {
    if (!template) return;
    const zones = parseZones(template.zones);
    zones[zi] = { ...zones[zi], [prop]: key || undefined };
    saveSchema(fields, { zones, text_layers: parseZones(template.text_layers) });
  };

  const unboundFields = useMemo(() => {
    if (!template) return [];
    const bound = collectBindings(template);
    return fields.filter(f => !bound.includes(f.key));
  }, [template, fields]);

  // ── Signs ───────────────────────────────────────────────────────────────────
  const addSign = async () => {
    if (!template) return;
    const name = window.prompt(L('Screen name (e.g. Room 204)', 'اسم الشاشة (مثلاً غرفة 204)'), '');
    if (!name) return;
    try {
      const r = await api.post('/info-screens', {
        brand_id: parseInt(brandId, 10),
        template_slide_id: template.id,
        sign_name: name,
        // Seed with the template's example values so a new screen renders
        // something recognisable before anyone types into it.
        field_values: signFieldDefaults(fields),
        orientation: template.orientation === 'V' ? 'PORTRAIT' : 'LANDSCAPE',
      });
      setSigns(s => [...s, r.data]);
      setSelSignId(r.data.id);
    } catch (e) { toast.error(e.response?.data?.error || 'Create failed'); }
  };

  const patchSign = async (id, patch) => {
    setSigns(ss => ss.map(s => (s.id === id ? { ...s, ...patch } : s)));   // optimistic
    try {
      const r = await api.put(`/info-screens/${id}`, patch);
      setSigns(ss => ss.map(s => (s.id === id ? r.data : s)));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
      await loadSigns(templateId);
    }
  };

  const setSignValue = (sign, key, value) =>
    patchSign(sign.id, { field_values: { ...(sign.field_values || {}), [key]: value } });

  const deleteSign = async (sign) => {
    if (!window.confirm(L(`Delete "${sign.sign_name}"? Its image is removed from the screen too.`,
                          `تمسح "${sign.sign_name}"؟ صورته هتتشال من الشاشة كمان.`))) return;
    try {
      await api.delete(`/info-screens/${sign.id}`);
      setSigns(ss => ss.filter(s => s.id !== sign.id));
      if (selSignId === sign.id) setSelSignId(null);
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const duplicateSign = async (sign) => {
    try {
      const r = await api.post('/info-screens', {
        brand_id: parseInt(brandId, 10),
        template_slide_id: template.id,
        sign_name: `${sign.sign_name} (copy)`,
        group_name: sign.group_name,
        branch_id: sign.branch_id,
        field_values: sign.field_values,
        sort_order: (sign.sort_order || 0) + 1,
      });
      setSigns(s => [...s, r.data]);
      setSelSignId(r.data.id);
    } catch (e) { toast.error(e.response?.data?.error || 'Duplicate failed'); }
  };

  // ── Per-sign photo upload ───────────────────────────────────────────────────
  // Reuses the zone-bg pipeline (resize + webp). zone_num is the SIGN id, which
  // keeps one file per door instead of every door overwriting the same name.
  const pickPhoto = (signId, fieldKey) => {
    pendingPhotoRef.current = { signId, fieldKey };
    photoInputRef.current?.click();
  };
  const onPhotoChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const pending = pendingPhotoRef.current;
    if (!file || !pending || !template) return;
    const sign = signs.find(s => s.id === pending.signId);
    if (!sign) return;
    setBusy(L('Uploading photo…', 'جاري رفع الصورة…'));
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('slide_name', `doorsign-${template.id}`);
      fd.append('zone_num', String(sign.id));
      fd.append('brand_id', brandId);
      const r = await api.post('/upload/zone-bg', fd);
      await setSignValue(sign, pending.fieldKey, `${r.data.url}?t=${Date.now()}`);
      toast.success(L('Photo updated', 'الصورة اتحدّثت'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setBusy(''); }
  };

  // ── Publish ─────────────────────────────────────────────────────────────────
  // Render this sign's own image from the template, then (if it already has a
  // screen assigned) push it. A sign with no screen yet is still rendered, so
  // the thumbnail and the "Send to screen" step both have something to work with.
  // Render the sign to an image. Not what goes on the screen any more — the
  // screen gets a live page — but it gives the card a preview for signs with no
  // screen assigned yet, and something to look at when a screen is offline.
  const renderPreview = async (sign) => {
    const bound  = applySignBindings(template, sign.field_values, fields, lang);
    const canvas = await renderSlideToCanvas(
      bound, template.orientation || 'H', menuData, restaurant, lang
    );
    const res = await api.post(`/info-screens/${sign.id}/save-jpeg`, {
      image_data: canvas.toDataURL('image/webp', 0.92),
    });
    return res.data;
  };

  // Publishing generates this sign's own page, puts it in its screen's folder
  // (clearing anything else — the folder is the playlist), and makes the screen
  // pull it now. After this, changing a value needs none of it: the page
  // re-reads its own data.
  const publishOne = async (sign) => {
    await renderPreview(sign).catch(() => null);
    if (!hasTarget(sign)) return { rendered: true, published: false };
    const { data } = await api.post(`/info-screens/${sign.id}/publish-html`, { lang });
    if (data?.orientation_warning) {
      toast(L(data.orientation_warning,
              'التمبلت ده مصمم لاتجاه تاني غير اتجاه الشاشة — التصميم هيتزنق.'),
            { icon: '⚠️', duration: 6000 });
    }
    return { rendered: true, published: true, ...data };
  };

  // Push the whole info-screen settings standard to this sign's screen, then make
  // it pull its folder immediately. One button turns a blank player into a
  // provisioned info screen.
  const deviceSetup = async (sign) => {
    if (!hasTarget(sign)) { toast.error(L('Pick a screen or group first', 'اختار شاشة أو مجموعة الأول')); return; }
    setBusy(L(`Setting up ${targetLabel(sign)}…`, `جاري تجهيز ${targetLabel(sign)}…`));
    try {
      const { data } = await api.post(`/info-screens/${sign.id}/device-setup`, { lang });
      const bad = data.settings?.mismatched || [];
      if (bad.length) {
        toast.error(L(`${bad.length} setting(s) did not stick: ${bad.map(m => m.key).join(', ')}`,
                      `${bad.length} إعداد ما اتطبقش: ${bad.map(m => m.key).join('، ')}`));
      } else if (data.publish?.error) {
        toast.error(L(`Settings applied, page failed: ${data.publish.error}`,
                      `الإعدادات اتطبقت، الصفحة فشلت: ${data.publish.error}`));
      } else {
        toast.success(L(`Screen ready — ${data.settings?.applied} settings + page published`,
                        `الشاشة جاهزة — ${data.settings?.applied} إعداد + الصفحة اتنشرت`));
      }
      await loadSigns(templateId);
      setTimeout(() => bumpShot(sign.id), 4000);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  // Provision every screen in this set in one pass. Screens with no device
  // assigned are skipped rather than counted as failures — they simply are not
  // ready to be set up yet.
  const setupAll = async () => {
    const targets = signs.filter(hasTarget);
    if (!targets.length) { toast.error(L('No screen has a device yet', 'مفيش شاشة متعيّن لها جهاز')); return; }
    let ok = 0, fail = 0;
    for (let i = 0; i < targets.length; i++) {
      setBusy(L(`Setting up ${i + 1}/${targets.length}…`, `جاري التجهيز ${i + 1}/${targets.length}…`));
      try { await api.post(`/info-screens/${targets[i].id}/device-setup`, { lang }); ok++; }
      catch { fail++; }
      bumpShot(targets[i].id);
    }
    setBusy('');
    await loadSigns(templateId);
    if (fail) toast.error(L(`${ok} ready, ${fail} failed`, `${ok} جاهزة، ${fail} فشلت`));
    else toast.success(L(`${ok} screen(s) ready`, `${ok} شاشة جاهزة`));
  };

  const deleteTemplate = async () => {
    if (!template) return;
    const n = signs.length;
    if (!window.confirm(L(
      `Delete "${template.slide_name}"?${n ? ` Its ${n} screen(s) lose their template and stop publishing.` : ''}`,
      `تمسح "${template.slide_name}"؟${n ? ` الـ ${n} شاشة هيفقدوا تمبلتهم ويوقفوا عن النشر.` : ''}`))) return;
    try {
      await api.delete(`/brand-slides/${template.id}`);
      const tpls = await loadTemplates();
      setTemplateId(tpls[0]?.id || null);
      await loadSigns(tpls[0]?.id || null);
      toast.success(L('Template deleted', 'التمبلت اتمسح'));
    } catch (e) { toast.error(e.response?.data?.error || 'Delete failed'); }
  };

  const syncNow = async (sign) => {
    if (!hasTarget(sign)) { toast.error(L('Pick a screen or group first', 'اختار شاشة أو مجموعة الأول')); return; }
    setBusy(L('Syncing screen…', 'جاري مزامنة الشاشة…'));
    try {
      await api.post(`/info-screens/${sign.id}/sync-now`, {});
      toast.success(L('Screen pulled its folder', 'الشاشة سحبت فولدرها'));
      setTimeout(() => bumpShot(sign.id), 5000);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const publishSign = async (sign) => {
    setBusy(L(`Publishing ${sign.sign_name}…`, `جاري نشر ${sign.sign_name}…`));
    try {
      const r = await publishOne(sign);
      await loadSigns(templateId);
      setTimeout(() => bumpShot(sign.id), 6000);
      toast.success(r.published
        ? L(`Live on ${targetLabel(sign)} · ${(r.bytes / 1024).toFixed(1)} KB`,
            `شغّال على ${targetLabel(sign)} · ${(r.bytes / 1024).toFixed(1)} كيلو`)
        : L('Rendered — pick a screen to publish it', 'اترسم — اختار شاشة عشان يتنشر'));
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const publishAll = async () => {
    if (!signs.length) return;
    const active = signs.filter(s => s.is_active !== false);
    let ok = 0, fail = 0;
    for (let i = 0; i < active.length; i++) {
      setBusy(L(`Publishing ${i + 1}/${active.length}…`, `جاري النشر ${i + 1}/${active.length}…`));
      try { await publishOne(active[i]); ok++; } catch { fail++; }
      bumpShot(active[i].id);
    }
    setBusy('');
    await loadSigns(templateId);
    if (fail) toast.error(L(`${ok} published, ${fail} failed`, `${ok} اتنشروا، ${fail} فشلوا`));
    else toast.success(L(`${ok} sign(s) published`, `${ok} ساين اتنشروا`));
  };

  // ── Preview ─────────────────────────────────────────────────────────────────
  // Design time shows the field defaults (the dummy data); with a sign selected
  // it shows that door's real values. Same function that renders the export, so
  // what you see is what lands on the door.
  const previewSlide = useMemo(() => {
    if (!template) return null;
    const values = selSign ? (selSign.field_values || {}) : signFieldDefaults(fields);
    const bound  = applySignBindings(template, values, fields, lang);
    // SlidePreview draws zone.items; only renderSlideToCanvas resolves item_ids
    // into them. Resolve here too, otherwise a template that also lists menu
    // items would preview empty and publish full.
    return {
      ...bound,
      zones: bound.zones.map(z => ({
        ...z,
        items: (z.item_ids || []).map(id => {
          const item = menuData.allItems?.find(i => i.id === id);
          if (!item) return null;
          const cat = menuData.categories?.find(c => c.id === item.category_id);
          return { ...item, cat_name: cat?.name || '', cat_name_ar: cat?.name_ar || '' };
        }).filter(Boolean),
      })),
    };
  }, [template, selSign, fields, lang, menuData]);

  const previewDim = template ? dimFor(template) : { w: 540, h: 960 };
  const previewScale = Math.min(1, 300 / previewDim.w, 420 / previewDim.h);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;

  return (
    <div className="page" style={{ direction: isAr ? 'rtl' : 'ltr' }}>
      <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoChosen} style={{ display: 'none' }} />

      {/* ── Template library ──────────────────────────────────────────────
          A catalogue, not a picker: every card previews with its own sample
          values so the customer sees a finished screen, not an empty frame. */}
      {showLib && (
        <div onClick={() => { setShowLib(false); setLibPick(null); setLibOpen(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 2000,
                   display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, width: 'min(1000px, 100%)',
                     maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                     direction: isAr ? 'rtl' : 'ltr' }}>

            <div style={{ padding: '14px 18px', borderBottom: '1px solid #EEE',
                          display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {L('Template library', 'مكتبة التمبلتات')}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  {L('Pick one and it is copied into this brand — yours to change.',
                     'اختار واحدة وهتتنسخ للبراند ده — بقت بتاعتك تعدّل فيها.')}
                </div>
              </div>
              <button style={btn()} onClick={() => { setShowLib(false); setLibPick(null); setLibOpen(null); }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: 6, padding: '10px 18px', flexWrap: 'wrap',
                          borderBottom: '1px solid #F3F4F6' }}>
              {SECTORS.map(sc => {
                const n = libItems.filter(i => i.library_sector === sc.k).length;
                if (!n) return null;
                return (
                  <button key={sc.k} onClick={() => { setLibSector(sc.k); setLibPick(null); setLibOpen(null); }}
                    style={{ padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12,
                             fontWeight: 700, border: '1.5px solid ' + (libSector === sc.k ? '#1565C0' : '#E5E7EB'),
                             background: libSector === sc.k ? '#1565C0' : '#fff',
                             color: libSector === sc.k ? '#fff' : '#555' }}>
                    {isAr ? sc.l_ar : sc.l} <span style={{ opacity: .7 }}>{n}</span>
                  </button>
                );
              })}
            </div>

            {libOpen ? (() => {
              const item  = libItems.find(i => i.id === libOpen);
              if (!item) return null;
              const bound = applySignBindings(item, signFieldDefaults(item.sign_fields || []),
                                              item.sign_fields || [], lang);
              const dim = { w: (item.width_px || 720) / 2, h: (item.height_px || 1280) / 2 };
              // Fill the room available without ever growing past 1:1.
              const sc  = Math.min(BIG_W / dim.w, BIG_H / dim.h, 1);
              const kinds = {};
              (item.sign_fields || []).forEach(f => { kinds[f.type] = (kinds[f.type] || 0) + 1; });
              return (
                <div style={{ padding: 16, overflowY: 'auto', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 320px', display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: dim.w * sc, height: dim.h * sc, overflow: 'hidden',
                                  background: item.bg_color || '#fff', borderRadius: 10,
                                  boxShadow: '0 6px 28px rgba(0,0,0,0.18)' }}>
                      <div style={{ width: dim.w, height: dim.h,
                                    transform: `scale(${sc})`, transformOrigin: 'top left' }}>
                        <SlidePreview
                          restaurant={restaurant}
                          zones={bound.zones} zone_cols={bound.zone_cols} zone_rows={bound.zone_rows}
                          zone_mode={bound.zone_mode} bgColor={bound.bg_color}
                          accentColor={bound.accent_color} orientation={item.orientation || 'V'}
                          lang={lang} slideBgImage={bound.slide_bg_image}
                          slideBgOverlay={bound.slide_bg_overlay}
                          widthPx={item.width_px} heightPx={item.height_px}
                          textLayers={bound.text_layers} slideKind="image" />
                      </div>
                    </div>
                  </div>

                  <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>{item.slide_name}</div>
                      <div style={{ fontSize: 13.5, color: '#4B5563', marginTop: 5, lineHeight: 1.55 }}>
                        {isAr ? (item.library_desc_ar || item.library_desc) : item.library_desc}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
                      <span style={chip}>{item.orientation === 'H' ? L('Wall screen', 'شاشة حائط') : L('Door panel', 'باب')}</span>
                      <span style={chip}>{item.width_px}×{item.height_px}</span>
                      {Object.entries(kinds).map(([k, n]) => (
                        <span key={k} style={chip}>{n} {FIELD_TYPES.find(t => t.v === k)
                          ? (isAr ? FIELD_TYPES.find(t => t.v === k).l_ar : FIELD_TYPES.find(t => t.v === k).l)
                          : k}</span>
                      ))}
                    </div>

                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase',
                                    letterSpacing: 0.6, marginBottom: 6 }}>
                        {L('What you fill in', 'اللي بتملاه')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 260, overflowY: 'auto' }}>
                        {(item.sign_fields || []).filter(fd => !LIVE_TYPES.includes(fd.type)).map(fd => (
                          <div key={fd.key} style={{ display: 'flex', gap: 8, fontSize: 12.5, alignItems: 'baseline' }}>
                            <span style={{ minWidth: 130, color: '#374151', fontWeight: 600 }}>
                              {(isAr ? fd.label_ar : fd.label) || fd.key}
                            </span>
                            <span style={{ color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fd.type === 'status'
                                ? (fd.options || []).map(o => (isAr ? o.label_ar : o.label)).join(' · ')
                                : fd.type === 'list'
                                  ? L(`${(fd.columns || []).length} columns · up to ${fd.max_rows || 6} rows`,
                                      `${(fd.columns || []).length} أعمدة · لحد ${fd.max_rows || 6} صفوف`)
                                  : (Array.isArray(fd.default) ? '' : (fd.default || ''))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 6 }}>
                      <button style={{ ...primaryBtn, flex: 1 }} disabled={busy === 'lib'}
                        onClick={() => takeLibraryTemplate(item)}>
                        {busy === 'lib' ? L('Copying…', 'بينسخ…') : L('Use this template', 'استخدم دي')}
                      </button>
                      <button style={btn()} onClick={() => setLibOpen(null)}>
                        {L('Back', 'رجوع')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })() : (
            <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 14,
                          gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
              {!libItems.length && <Empty>{L('Loading…', 'بيحمّل…')}</Empty>}
              {libItems.filter(i => i.library_sector === libSector).map(item => {
                const bound = applySignBindings(item, signFieldDefaults(item.sign_fields || []),
                                                item.sign_fields || [], lang);
                const picked = libPick === item.id;
                // Design space is half the output. Scale to fit the card on both
                // axes so a tall door and a wide wall board sit on the same row.
                const dim = { w: (item.width_px || 720) / 2, h: (item.height_px || 1280) / 2 };
                const sc  = Math.min(THUMB_W / dim.w, THUMB_H / dim.h);
                return (
                  <div key={item.id} onClick={() => setLibOpen(item.id)}
                    title={L('Open a full-size preview', 'افتح معاينة كبيرة')}
                    style={{ border: '2px solid ' + (picked ? '#1565C0' : '#E5E7EB'), borderRadius: 12,
                             padding: 10, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8,
                             background: picked ? '#F5F9FF' : '#fff' }}>
                    <div style={{ background: '#F3F4F6', borderRadius: 8, height: THUMB_H + 12,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: dim.w * sc, height: dim.h * sc, overflow: 'hidden',
                                    background: item.bg_color || '#fff', borderRadius: 4,
                                    boxShadow: '0 1px 6px rgba(0,0,0,0.12)' }}>
                        <div style={{ width: dim.w, height: dim.h,
                                      transform: `scale(${sc})`, transformOrigin: 'top left' }}>
                          <SlidePreview
                            restaurant={restaurant}
                            zones={bound.zones}
                            zone_cols={bound.zone_cols}
                            zone_rows={bound.zone_rows}
                            zone_mode={bound.zone_mode}
                            bgColor={bound.bg_color}
                            accentColor={bound.accent_color}
                            orientation={item.orientation || 'V'}
                            lang={lang}
                            slideBgImage={bound.slide_bg_image}
                            slideBgOverlay={bound.slide_bg_overlay}
                            widthPx={item.width_px}
                            heightPx={item.height_px}
                            textLayers={bound.text_layers}
                            slideKind="image"
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}>
                      {item.slide_name}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#6B7280', lineHeight: 1.45, flex: 1 }}>
                      {isAr ? (item.library_desc_ar || item.library_desc) : item.library_desc}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 10.5, color: '#9CA3AF' }}>
                      <span>{item.orientation === 'H' ? L('Wall screen', 'شاشة حائط') : L('Door panel', 'باب')}</span>
                      <span>·</span>
                      <span>{(item.sign_fields || []).length} {L('fields', 'حقل')}</span>
                      {(item.zones || []).some(z => z.list_binding) && (
                        <><span>·</span><span>{L('list', 'قايمة')}</span></>
                      )}
                    </div>
                    <button style={{ ...btn({ padding: '6px 10px' }), width: '100%' }}
                      onClick={e => { e.stopPropagation(); setLibOpen(item.id); }}>
                      {L('Preview', 'معاينة')}
                    </button>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      )}

      {/* ── Header — same shape as the Slides page ──────────────────────── */}
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <BrandNav brandId={brandId} subPath="info-screens" />
          <h1 className="page-title">
            {restaurant?.name}
            <span style={{ fontWeight: 400, color: '#999', fontSize: 16, marginLeft: 8 }}>
              — {L('Info Screens', 'شاشات المعلومات')}
            </span>
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
            {['en','ar'].map(l => (
              <button key={l} onClick={() => setLang(l)}
                style={{ padding: '5px 10px', border: 'none', background: lang === l ? '#1565C0' : '#fff',
                         color: lang === l ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <div style={{ position: 'relative' }} ref={pageMenuRef}>
            <button onClick={() => setShowPageMenu(p => !p)}
              style={{ height: 36, width: 36, borderRadius: 9, border: '1.5px solid #E0E0E0',
                       background: showPageMenu ? '#E3F2FD' : '#fff', cursor: 'pointer',
                       display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444' }}>
              <IcoHamburger />
            </button>
            {showPageMenu && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 1000,
                            background: '#fff', borderRadius: 12, boxShadow: '0 6px 28px rgba(0,0,0,0.16)',
                            border: '1px solid #E8E8E8', minWidth: 230, padding: '6px 0' }}>
                <button onClick={() => { setShowPageMenu(false); openLibrary(); }} style={pageMenuItemStyle()}>
                  <span style={{ fontSize: 15 }}>🪧</span> {L('Browse the library', 'تصفّح المكتبة')}
                </button>
                <button onClick={() => { setShowPageMenu(false); createTemplate(); }} style={pageMenuItemStyle()}>
                  <span style={{ fontSize: 15 }}>➕</span> {L('New template', 'تمبلت جديد')}
                </button>
                {template && (
                  <button onClick={() => { setShowPageMenu(false); navigate(`/brands/${brandId}/slide-builder/${template.id}?from=info-screens`); }}
                    style={pageMenuItemStyle()}>
                    <span style={{ fontSize: 15 }}>✎</span> {L('Design template', 'صمّم التمبلت')}
                  </button>
                )}

                <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />

                <button onClick={() => { setShowPageMenu(false); addSign(); }}
                  disabled={!template} style={pageMenuItemStyle(false, !template)}>
                  <span style={{ fontSize: 15 }}>🪧</span> {L('New screen', 'شاشة جديدة')}
                </button>
                <button onClick={() => { setShowPageMenu(false); publishAll(); }}
                  disabled={!signs.length || !!busy} style={pageMenuItemStyle(false, !signs.length || !!busy)}>
                  <span style={{ fontSize: 15 }}>⬆</span> {L('Publish all', 'انشر الكل')}
                </button>
                <button onClick={() => { setShowPageMenu(false); setupAll(); }}
                  disabled={!signs.some(hasTarget) || !!busy}
                  style={pageMenuItemStyle(false, !signs.some(hasTarget) || !!busy)}>
                  <span style={{ fontSize: 15 }}>⚙︎</span> {L('Device Setup — all screens', 'تجهيز كل الشاشات')}
                </button>

                {template && (
                  <>
                    <div style={{ height: 1, background: '#F0F0F0', margin: '4px 0' }} />
                    <button onClick={() => { setShowPageMenu(false); deleteTemplate(); }} style={pageMenuItemStyle(true)}>
                      <span style={{ fontSize: 15 }}>🗑</span> {L('Delete template', 'امسح التمبلت')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Template picker + the two sections ──────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#666' }}>{L('TEMPLATE', 'التمبلت')}</span>
        <select value={templateId || ''} onChange={e => switchTemplate(parseInt(e.target.value, 10))}
          style={{ ...input, width: 'auto', minWidth: 200 }}>
          {!templates.length && <option value="">{L('— none yet —', '— لسه مفيش —')}</option>}
          {templates.map(t => <option key={t.id} value={t.id}>{t.slide_name}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {template && (
          <div style={{ display: 'flex', border: '1.5px solid #E0E0E0', borderRadius: 8, overflow: 'hidden' }}>
            <button onClick={() => setTab('templates')}
              style={{ padding: '7px 16px', border: 'none', background: tab === 'templates' ? '#1565C0' : '#fff',
                       color: tab === 'templates' ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {L('Template', 'التمبلت')} ({fields.length})
            </button>
            <button onClick={() => setTab('screens')}
              style={{ padding: '7px 16px', border: 'none', background: tab === 'screens' ? '#1565C0' : '#fff',
                       color: tab === 'screens' ? '#fff' : '#555', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
              {L('Screens', 'الشاشات')} ({signs.length})
            </button>
          </div>
        )}
      </div>

      {!template && (
        <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>
          {L('No template yet. Open the ☰ menu to create one, design it in the Slide Builder, then declare its fields here.',
             'مفيش تمبلت. افتح قايمة ☰ عشان تعمل واحد، صمّمه في السلايد بيلدر، وبعدين عرّف حقوله من هنا.')}
        </div>
      )}

      {template && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* ── Main pane ──────────────────────────────────────────────── */}
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>

            {tab === 'templates' && (
              <>
                <SectionHeader
                  title={L("What's on this screen?", 'إيه اللي على الشاشة؟')}
                  hint={L('Everything the template draws. Mark what changes from screen to screen — the rest stays as designed.',
                          'كل حاجة التمبلت بيرسمها. علّم اللي بيتغيّر من شاشة للتانية — والباقي يفضل زي ما صمّمته.')} />

                {!elements.length && (
                  <Empty>{L('Nothing on the template yet — design it in the Slide Builder first.',
                            'التمبلت لسه فاضي — صمّمه في السلايد بيلدر الأول.')}</Empty>
                )}

                {elements.map((el) => {
                  const f    = el.fieldKey ? fields.find(x => x.key === el.fieldKey) : null;
                  const mode = !f ? 'fixed' : (['status','list'].includes(f.type) ? f.type : 'fillable');
                  const modes = el.kind === 'color'
                    ? [['fixed', L('Fixed colour', 'لون ثابت')], ['status', L('Follows a status', 'يتبع حالة')],
                       ['list', L('Repeating list', 'قايمة متكررة')], ['prayer', L('Prayer times', 'مواقيت الصلاة')]]
                    : el.kind === 'list'
                    ? [['list', L('Repeating list', 'قايمة متكررة')], ['prayer', L('Prayer times', 'مواقيت الصلاة')],
                       ['fixed', L('Plain box', 'مربع عادي')]]
                    : el.kind === 'image'
                      ? [['fixed', L('Fixed image', 'صورة ثابتة')], ['fillable', L('Changes per screen', 'بيتغيّر لكل شاشة')],
                         ['qr', L('QR code', 'كود QR')]]
                      : [['fixed', L('Fixed text', 'نص ثابت')], ['fillable', L('Changes per screen', 'بيتغيّر لكل شاشة')],
                         ['status', L('Status', 'حالة')], ['clock', L('Clock', 'ساعة')], ['date', L('Date', 'تاريخ')],
                         ['prayer', L('Next prayer', 'الصلاة القادمة')],
                         ['countdown', L('Countdown', 'عدّاد')]];
                  const realMode = f ? (['status','clock','date','list','qr','prayer','countdown'].includes(f.type) ? f.type : 'fillable') : 'fixed';

                  return (
                    <div key={el.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6,
                                              padding: '8px 10px', border: '1px solid #EEE', borderRadius: 8,
                                              background: f ? '#F8FAFF' : '#fff', flexWrap: 'wrap' }}>
                      {/* what it looks like on the screen */}
                      {el.kind === 'color'
                        ? <span style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                                         background: el.swatch || 'transparent', border: '1px solid #E5E7EB' }} />
                        : el.kind === 'image'
                          ? (el.thumb
                              ? <img src={el.thumb} alt="" style={{ width: 30, height: 22, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                              : <span style={{ width: 30, height: 22, borderRadius: 4, background: '#F3F4F6', flexShrink: 0 }} />)
                          : <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', minWidth: 24 }}>{el.tag}</span>}

                      <span style={{ flex: '1 1 150px', minWidth: 0, fontSize: 12.5, overflow: 'hidden',
                                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {el.preview || <em style={{ color: '#bbb' }}>{el.fallback}</em>}
                      </span>

                      <select style={{ ...input, width: 168 }} value={realMode}
                        onChange={e => setElementMode(el, e.target.value)}>
                        {modes.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                      </select>

                      {/* named + sampled only when it actually varies */}
                      {f && (
                        <>
                          <input style={{ ...input, width: 140 }} value={isAr ? (f.label_ar || '') : (f.label || '')}
                            placeholder={L('Call it…', 'سمّيه…')}
                            onChange={e => patchFieldByKey(f.key, isAr ? { label_ar: e.target.value } : { label: e.target.value })} />
                          {f.type === 'text' && (
                            <input style={{ ...input, width: 150 }} value={f.default || ''}
                              placeholder={L('Example value', 'مثال للقيمة')}
                              onChange={e => patchFieldByKey(f.key, { default: e.target.value })} />
                          )}
                          {f.type === 'status' && (
                            <span style={{ fontSize: 11, color: '#6B7280' }}>
                              {(f.options || []).length} {L('states', 'حالة')}
                            </span>
                          )}
                          {f.type === 'list' && (
                            <>
                              <label style={{ fontSize: 11, color: '#6B7280' }}>
                                {L('max rows', 'أقصى صفوف')}
                                <input type="number" min="1" max="20"
                                  style={{ ...input, width: 56, marginInlineStart: 5 }}
                                  value={f.max_rows || 6}
                                  onChange={e => patchFieldByKey(f.key, { max_rows: parseInt(e.target.value, 10) || 6 })} />
                              </label>
                              <button style={btn({ padding: '4px 9px' })}
                                onClick={() => setColsFor(colsFor === f.key ? null : f.key)}>
                                {L('Columns', 'الأعمدة')} ({(f.columns || []).length})
                              </button>
                            </>
                          )}
                          {f.type === 'countdown' && (
                            <>
                              <select style={{ ...input, width: 140 }} value={f.mode || 'until'}
                                onChange={e => patchFieldByKey(f.key, { mode: e.target.value })}>
                                <option value="until">{L('Counts down to', 'عدّ تنازلي لـ')}</option>
                                <option value="since">{L('Counts up from', 'عدّ تصاعدي من')}</option>
                              </select>
                              <select style={{ ...input, width: 128 }} value={f.format || 'auto'}
                                onChange={e => patchFieldByKey(f.key, { format: e.target.value })}>
                                <option value="auto">{L('Automatic', 'تلقائي')}</option>
                                <option value="n">184</option>
                                <option value="days">184 days</option>
                                <option value="dh">2d 04h</option>
                                <option value="dhms">2d 04:15:33</option>
                                <option value="hms">04:15:33</option>
                                <option value="hm">04:15</option>
                              </select>
                              {f.mode !== 'since' && (
                                <input style={{ ...input, width: 120 }} value={f.expired || ''}
                                  placeholder={L('When it hits zero', 'لما يوصل صفر')}
                                  onChange={e => patchFieldByKey(f.key, { expired: e.target.value })} />
                              )}
                            </>
                          )}
                          {f.type === 'qr' && (
                            <input style={{ ...input, width: 190 }} value={f.default || ''}
                              placeholder={L('Example link', 'مثال للينك')}
                              onChange={e => patchFieldByKey(f.key, { default: e.target.value })} />
                          )}
                          {f.type === 'prayer' && (
                            <>
                              <select style={{ ...input, width: 132 }}
                                value={PRAYER_PLACES.findIndex(pl => pl.lat === f.lat && pl.lng === f.lng)}
                                onChange={e => {
                                  const pl = PRAYER_PLACES[parseInt(e.target.value, 10)];
                                  if (pl) patchFieldByKey(f.key, { lat: pl.lat, lng: pl.lng,
                                                                   tzname: pl.tzname, method: pl.method });
                                }}>
                                <option value="-1">{L('Custom location', 'مكان مخصص')}</option>
                                {PRAYER_PLACES.map((pl, i) => (
                                  <option key={pl.l} value={i}>{isAr ? pl.l_ar : pl.l}</option>
                                ))}
                              </select>
                              <button style={btn({ padding: '4px 9px' })}
                                onClick={() => setPrayFor(prayFor === f.key ? null : f.key)}>
                                {L('Settings', 'الإعدادات')}
                              </button>
                              {prayPreview[f.key] && (
                                <span style={{ fontSize: 11, color: '#1D4ED8', fontFamily: 'monospace' }}>
                                  {Object.entries(prayPreview[f.key]).map(([k, v]) => v).join(' · ')}
                                </span>
                              )}
                            </>
                          )}
                          {f.type === 'clock' && (
                            <select style={{ ...input, width: 132 }} value={f.format || 'hm'}
                              onChange={e => patchFieldByKey(f.key, { format: e.target.value })}>
                              <option value="hm">14:05</option>
                              <option value="hms">14:05:30</option>
                              <option value="h12">2:05 PM</option>
                              <option value="h12s">2:05:30 PM</option>
                            </select>
                          )}
                        </>
                      )}
                      {/* Where on earth, which convention, and which madhab —
                          everything the calculation needs and nothing else. */}
                      {f && f.type === 'prayer' && prayFor === f.key && (
                        <div style={{ flex: '1 1 100%', marginTop: 6, padding: 10, background: '#fff',
                                      border: '1px solid #E5E7EB', borderRadius: 8,
                                      display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {[['lat', L('Latitude', 'خط العرض')], ['lng', L('Longitude', 'خط الطول')]].map(([k, lbl]) => (
                            <label key={k} style={{ fontSize: 11, color: '#6B7280' }}>
                              {lbl}
                              <input type="number" step="0.0001" style={{ ...input, width: 96, marginInlineStart: 5 }}
                                value={f[k] ?? ''} onChange={e => patchFieldByKey(f.key, { [k]: parseFloat(e.target.value) })} />
                            </label>
                          ))}
                          <label style={{ fontSize: 11, color: '#6B7280' }}>
                            {L('Time zone', 'المنطقة الزمنية')}
                            <input style={{ ...input, width: 150, marginInlineStart: 5 }}
                              value={f.tzname || ''} placeholder="Africa/Cairo"
                              onChange={e => patchFieldByKey(f.key, { tzname: e.target.value.trim() })} />
                          </label>
                          <select style={{ ...input, width: 130 }} value={f.clock === '12' ? '12' : '24'}
                            onChange={e => patchFieldByKey(f.key, { clock: e.target.value })}>
                            <option value="24">{L('24-hour  16:25', '٢٤ ساعة  16:25')}</option>
                            <option value="12">{L('12-hour  4:25 PM', '١٢ ساعة  4:25 م')}</option>
                          </select>
                          <select style={{ ...input, width: 210 }} value={f.method || 'EGYPT'}
                            onChange={e => patchFieldByKey(f.key, { method: e.target.value })}>
                            {prayMethods.map(m => (
                              <option key={m.value} value={m.value}>{isAr ? m.label_ar : m.label}</option>
                            ))}
                          </select>
                          <select style={{ ...input, width: 150 }} value={f.madhab || 'standard'}
                            onChange={e => patchFieldByKey(f.key, { madhab: e.target.value })}>
                            <option value="standard">{L('Asr — standard', 'العصر — الجمهور')}</option>
                            <option value="hanafi">{L('Asr — Hanafi', 'العصر — الحنفي')}</option>
                          </select>
                          <label style={{ fontSize: 11, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input type="checkbox" checked={!!f.hide_sunrise}
                              onChange={e => patchFieldByKey(f.key, { hide_sunrise: e.target.checked })} />
                            {L('Hide sunrise', 'اخفي الشروق')}
                          </label>
                          <button style={btn({ padding: '4px 9px' })} onClick={() => loadPrayerPreview(f)}>
                            {L('Check times', 'اتأكد من المواقيت')}
                          </button>
                        </div>
                      )}

                      {/* Column layout — width, size, weight and colour per
                          column. Widths are percentages of the box, so the list
                          fits whatever panel it lands on. */}
                      {f && f.type === 'list' && colsFor === f.key && (
                        <div style={{ flex: '1 1 100%', marginTop: 6, padding: 8, background: '#fff',
                                      border: '1px solid #E5E7EB', borderRadius: 8 }}>
                          {(f.columns || []).map((c, ci) => (
                            <div key={c.key || ci} style={{ display: 'flex', gap: 5, alignItems: 'center', marginBottom: 5, flexWrap: 'wrap' }}>
                              <input style={{ ...input, width: 110 }} placeholder={L('Column name', 'اسم العمود')}
                                value={(isAr ? c.label_ar : c.label) || ''}
                                onChange={e => patchColumn(f, ci, isAr ? { label_ar: e.target.value } : { label: e.target.value })} />
                              <label style={{ fontSize: 10, color: '#6B7280' }}>%
                                <input type="number" min="5" max="100" style={{ ...input, width: 52, marginInlineStart: 3 }}
                                  value={c.width ?? 50}
                                  onChange={e => patchColumn(f, ci, { width: parseInt(e.target.value, 10) || 50 })} /></label>
                              <label style={{ fontSize: 10, color: '#6B7280' }}>{L('size', 'حجم')}
                                <input type="number" min="4" max="120" style={{ ...input, width: 52, marginInlineStart: 3 }}
                                  value={c.size ?? 15}
                                  onChange={e => patchColumn(f, ci, { size: parseInt(e.target.value, 10) || 15 })} /></label>
                              <select style={{ ...input, width: 82 }} value={c.weight || 'normal'}
                                onChange={e => patchColumn(f, ci, { weight: e.target.value })}>
                                <option value="normal">{L('Normal', 'عادي')}</option>
                                <option value="bold">{L('Bold', 'عريض')}</option>
                              </select>
                              <select style={{ ...input, width: 78 }} value={c.align || 'start'}
                                onChange={e => patchColumn(f, ci, { align: e.target.value })}>
                                <option value="start">{L('Left', 'يسار')}</option>
                                <option value="center">{L('Center', 'وسط')}</option>
                                <option value="end">{L('Right', 'يمين')}</option>
                              </select>
                              <input type="color" style={{ width: 30, height: 26, padding: 0, border: '1px solid #E5E7EB', borderRadius: 5 }}
                                value={c.color || '#12213A'}
                                onChange={e => patchColumn(f, ci, { color: e.target.value })} />
                              {(f.columns || []).length > 1 && (
                                <button style={btn({ padding: '3px 7px', color: '#DC2626', borderColor: '#FCA5A5' })}
                                  onClick={() => removeColumn(f, ci)}>✕</button>
                              )}
                            </div>
                          ))}
                          <button style={btn({ padding: '4px 9px' })} onClick={() => addColumn(f)}>
                            + {L('Column', 'عمود')}
                          </button>
                          <span style={{ fontSize: 10.5, color: '#9CA3AF', marginInlineStart: 8 }}>
                            {L('Widths are % of the box — keep the total near 100.',
                               'العرض بالنسبة المئوية من المربع — خلي المجموع قريب من 100.')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!!fields.length && (
                  <div style={{ marginTop: 10, padding: '8px 12px', background: '#F0FDF4',
                                border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 11.5, color: '#166534' }}>
                    ✓ {L(`${fields.length} thing(s) will be filled in per screen. Switch to the Screens tab to add one.`,
                          `${fields.length} حاجة هتتملّي لكل شاشة. روح تبويب الشاشات وضيف واحدة.`)}
                  </div>
                )}
              </>
            )}
            {tab === 'screens' && (
              <>
                <SectionHeader
                  title={L('Screens', 'الشاشات')}
                  hint={L('One row per physical screen. Each renders on its own and goes to its own device.',
                          'صف لكل شاشة. كل واحدة بتترسم لوحدها وبتروح لجهازها.')}
                  action={<button style={primaryBtn} onClick={addSign}>+ {L('Screen', 'شاشة')}</button>} />
                {!signs.length && <Empty>{L('No signs yet.', 'مفيش ساينات لسه.')}</Empty>}
                {signs.map(sign => {
                  const isSel = sign.id === selSignId;
                  const targets = sign.send_targets;
                  const grp = groupOf(sign);
                  return (
                    <div key={sign.id}
                      style={{ marginBottom: 8, border: `1px solid ${isSel ? '#1565C0' : '#EEE'}`,
                               borderRadius: 8, background: isSel ? '#F8FAFF' : '#fff' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                                    cursor: 'pointer', flexWrap: 'wrap' }}
                        onClick={() => setSelSignId(isSel ? null : sign.id)}>
                        <ScreenThumb sign={sign} ver={shotVer[sign.id]} onRefresh={() => bumpShot(sign.id)} />
                        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sign.sign_name}
                          </div>
                          <div style={{ fontSize: 10, color: '#999',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sign.group_name || L('no section', 'بدون قسم')}
                            {' · '}
                            {grp
                              ? L(`${grp.name} · ${(grp.device_names || []).length} screens`,
                                  `${grp.name} · ${(grp.device_names || []).length} شاشة`)
                              : (sign.device_name || L('no screen', 'بدون شاشة'))}
                            {sign.published_at && ` · ${new Date(sign.published_at).toLocaleDateString()}`}
                          </div>
                        </div>
                        {/* Picker + actions travel together: on a phone they drop
                            to their own full-width line under the name instead of
                            crushing it. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                                      flex: isNarrow ? '1 1 100%' : '0 0 auto',
                                      justifyContent: isNarrow ? 'space-between' : 'flex-end' }}>
{/* One picker for both kinds of target. The value carries which it is, so
    choosing a group clears the device and the other way round. */}
                        <select
                          value={sign.group_id ? `g:${sign.group_id}` : (sign.device_name ? `d:${sign.device_name}` : '')}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            e.stopPropagation();
                            const v = e.target.value;
                            if (v.startsWith('g:')) patchSign(sign.id, { group_id: parseInt(v.slice(2), 10), device_name: '' });
                            else                    patchSign(sign.id, { device_name: v.slice(2), group_id: null });
                          }}
                          title={L('Target screen or group', 'الشاشة أو المجموعة')}
                          style={{ ...input, ...(isNarrow ? { flex: '1 1 auto', minWidth: 0 } : { width: 152 }),
                                   height: 26, fontSize: 11 }}>
                          <option value="">{L('— no screen —', '— بدون شاشة —')}</option>
                          {!!devices.length && (
                            <optgroup label={L('Screens', 'شاشات')}>
                              {devices.map(d => (
                                <option key={d.device_name} value={`d:${d.device_name}`}>{d.device_name}</option>
                              ))}
                            </optgroup>
                          )}
                          {!!groups.length && (
                            <optgroup label={L('Groups', 'مجموعات')}>
                              {groups.map(g => (
                                <option key={g.id} value={`g:${g.id}`}>
                                  {g.name} ({(g.device_names || []).length})
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        <button style={btn({ padding: '4px 8px', borderColor: '#A7F3D0', color: '#047857' })}
                          onClick={e => { e.stopPropagation(); deviceSetup(sign); }}
                          disabled={!!busy || !hasTarget(sign)}
                          title={L('Device Setup — apply the info-screen settings standard AND publish this screen',
                                   'ديفايس سِت — يطبّق إعدادات الدور-ساين وينشر الساين على الشاشة')}>⚙︎</button>
                        <button style={btn({ padding: '4px 8px' })}
                          onClick={e => { e.stopPropagation(); syncNow(sign); }}
                          disabled={!!busy || !hasTarget(sign)}
                          title={L('Sync now', 'زامن دلوقتي')}>⟳</button>
                        <button style={btn({ padding: '4px 8px' })}
                          onClick={e => { e.stopPropagation(); publishSign(sign); }}
                          disabled={!!busy} title={L('Publish', 'انشر')}>⬆</button>
                        <button style={btn({ padding: '4px 8px' })}
                          onClick={e => { e.stopPropagation(); duplicateSign(sign); }} title="Duplicate">⎘</button>
                        <button style={btn({ padding: '4px 8px', borderColor: '#FCA5A5', color: '#DC2626' })}
                          onClick={e => { e.stopPropagation(); deleteSign(sign); }}>✕</button>
                        </div>
                      </div>

                      {isSel && (
                        <div style={{ padding: '8px 12px', borderTop: '1px solid #EEE',
                                      display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <input style={{ ...input, flex: '1 1 160px' }} value={sign.sign_name || ''}
                              placeholder={L('Screen name', 'اسم الشاشة')}
                              onChange={e => setSigns(ss => ss.map(s => s.id === sign.id ? { ...s, sign_name: e.target.value } : s))}
                              onBlur={e => patchSign(sign.id, { sign_name: e.target.value })} />
                            <input style={{ ...input, flex: '1 1 140px' }} value={sign.group_name || ''}
                              placeholder={L('Section / floor', 'القسم / الدور')}
                              onChange={e => setSigns(ss => ss.map(s => s.id === sign.id ? { ...s, group_name: e.target.value } : s))}
                              onBlur={e => patchSign(sign.id, { group_name: e.target.value })} />
                          </div>
                          {!fields.length && (
                            <Empty>{L('Define fields first (Fields & bindings tab).',
                                      'عرّف الحقول الأول (تبويب الحقول والربط).')}</Empty>
                          )}
                          {fields.map(f => (
                            <div key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <label style={{ fontSize: 11, color: '#666', minWidth: 90, fontWeight: 600 }}>
                                {(isAr ? f.label_ar : f.label) || f.key}
                              </label>
                              {LIVE_TYPES.includes(f.type) ? (
                                <span style={{ flex: 1, fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>
                                  {L('live — nothing to fill in', 'حي — مفيش حاجة تتملى')}
                                </span>
                              ) : f.type === 'status' ? (
                                <select style={{ ...input, flex: 1 }}
                                  value={(sign.field_values || {})[f.key] ?? f.default ?? ''}
                                  onChange={e => setSignValue(sign, f.key, e.target.value)}>
                                  {(f.options || []).map(o => (
                                    <option key={o.value} value={o.value}>{(isAr ? o.label_ar : o.label) || o.value}</option>
                                  ))}
                                </select>
                              ) : f.type === 'list' ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {(rowsOf(sign, f)).map((row, ri) => (
                                    <div key={ri} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                      {(f.columns || []).map(c => (
                                        <input key={c.key} style={{ ...input, flex: `1 1 ${c.width || 50}%`, minWidth: 0 }}
                                          value={row[c.key] ?? ''}
                                          placeholder={(isAr ? c.label_ar : c.label) || c.key}
                                          onChange={e => patchRow(sign, f, ri, { [c.key]: e.target.value })} />
                                      ))}
                                      <button style={btn({ padding: '4px 7px', color: '#DC2626', borderColor: '#FCA5A5' })}
                                        onClick={() => removeRow(sign, f, ri)} title={L('Remove row', 'شيل الصف')}>✕</button>
                                    </div>
                                  ))}
                                  <div>
                                    <button style={btn({ padding: '4px 9px' })}
                                      disabled={rowsOf(sign, f).length >= (f.max_rows || 6)}
                                      onClick={() => addRow(sign, f)}>
                                      + {L('Row', 'صف')} ({rowsOf(sign, f).length}/{f.max_rows || 6})
                                    </button>
                                  </div>
                                </div>
                              ) : f.type === 'countdown' ? (
                                <input type="datetime-local" style={{ ...input, flex: 1 }}
                                  value={String((sign.field_values || {})[f.key] ?? f.default ?? '').replace(' ', 'T').slice(0, 16)}
                                  onChange={e => setSignValue(sign, f.key, e.target.value)}
                                  onBlur={e => patchSign(sign.id, { field_values: { ...(sign.field_values || {}), [f.key]: e.target.value } })} />
                              ) : f.type === 'qr' ? (
                                <input style={{ ...input, flex: 1 }}
                                  value={(sign.field_values || {})[f.key] ?? ''}
                                  placeholder={f.default || L('Link or text to encode…', 'لينك أو نص يتحوّل لكود…')}
                                  onChange={e => setSignValue(sign, f.key, e.target.value)}
                                  onBlur={e => patchSign(sign.id, { field_values: { ...(sign.field_values || {}), [f.key]: e.target.value } })} />
                              ) : f.type === 'image' ? (
                                <span style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                                  {(sign.field_values || {})[f.key] && (
                                    <img src={(sign.field_values || {})[f.key]} alt=""
                                      style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                                  )}
                                  <button style={btn()} onClick={() => pickPhoto(sign.id, f.key)} disabled={!!busy}>
                                    {L('Choose photo…', 'اختار صورة…')}
                                  </button>
                                  {(sign.field_values || {})[f.key] && (
                                    <button style={btn({ padding: '4px 8px', color: '#DC2626', borderColor: '#FCA5A5' })}
                                      onClick={() => setSignValue(sign, f.key, '')}>✕</button>
                                  )}
                                </span>
                              ) : (
                                <input style={{ ...input, flex: 1 }}
                                  value={(sign.field_values || {})[f.key] ?? ''}
                                  placeholder={f.default || ''}
                                  onChange={e => setSigns(ss => ss.map(s => s.id === sign.id
                                    ? { ...s, field_values: { ...(s.field_values || {}), [f.key]: e.target.value } } : s))}
                                  onBlur={e => setSignValue(sign, f.key, e.target.value)} />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* ── Live preview ───────────────────────────────────────────── */}
          <div style={{ flex: '0 0 auto', position: 'sticky', top: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#666', marginBottom: 6 }}>
              {selSign
                ? `${L('Preview', 'معاينة')} · ${selSign.sign_name}`
                : `${L('Preview', 'معاينة')} · ${L('dummy data', 'بيانات دومي')}`}
            </div>
            <div style={{ width: previewDim.w * previewScale, height: previewDim.h * previewScale,
                          border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden',
                          background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <div style={{ width: previewDim.w, height: previewDim.h,
                            transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
                {previewSlide && (
                  <SlidePreview
                    restaurant={restaurant}
                    zones={previewSlide.zones}
                    zone_cols={previewSlide.zone_cols}
                    zone_rows={previewSlide.zone_rows}
                    zone_mode={previewSlide.zone_mode}
                    bgColor={previewSlide.bg_color}
                    accentColor={previewSlide.accent_color}
                    orientation={previewSlide.orientation || 'V'}
                    lang={lang}
                    slideBgImage={previewSlide.slide_bg_image}
                    slideBgOverlay={previewSlide.slide_bg_overlay}
                    widthPx={previewSlide.width_px}
                    heightPx={previewSlide.height_px}
                    textLayers={previewSlide.text_layers}
                    slideKind="image"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {sendFor && (
        <SendToTVModal
          slides={signs}
          slideIds={sendFor}
          brandId={brandId}
          endpoint="info-screens"
          onClose={() => { setSendFor(null); loadSigns(templateId); }}
          onSent={(id, targets) => setSigns(ss => ss.map(s => (s.id === id ? { ...s, send_targets: targets } : s)))}
        />
      )}

      {busy && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(0,0,0,0.45)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', padding: '18px 26px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
            {busy}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, hint, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 8px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{title}</div>
        {hint && <div style={{ fontSize: 10, color: '#9CA3AF' }}>{hint}</div>}
      </div>
      <div style={{ flex: 1 }} />
      {action}
    </div>
  );
}

// Live screen thumbnail. Asks the device for a screenshot through the MQTT
// bridge; falls back to the rendered preview when the screen is offline or has
// none assigned, so the card is never an empty box.
//
// Fetched as a blob rather than set as an <img src> directly: the endpoint is
// auth-gated and a bare img tag would not carry the bearer token.
function ScreenThumb({ sign, ver, onRefresh }) {
  const [liveUrl, setLiveUrl] = useState(null);
  const [failed,  setFailed]  = useState(false);

  useEffect(() => {
    const assigned = !!(sign.device_name || sign.group_id);
    if (!assigned) { setLiveUrl(null); return; }
    let dead = false, objUrl = null;
    setFailed(false);
    api.get(`/info-screens/${sign.id}/screenshot.jpg`, { responseType: 'blob', timeout: 60000 })
      .then(r => {
        if (dead) return;
        objUrl = URL.createObjectURL(r.data);
        setLiveUrl(objUrl);
      })
      .catch(() => { if (!dead) { setFailed(true); setLiveUrl(null); } });
    return () => { dead = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [sign.id, sign.device_name, sign.group_id, ver]);

  const box = {
    width: 64, height: 40, borderRadius: 4, border: '1px solid #E5E7EB',
    objectFit: 'cover', background: '#F3F4F6', flexShrink: 0, display: 'block',
    cursor: (sign.device_name || sign.group_id) ? 'pointer' : 'default',
  };
  const src = liveUrl || (failed ? (sign.thumb_path || '') : (sign.thumb_path || ''));
  const isLive = !!liveUrl;

  if (!src) {
    return <div style={{ ...box, border: '1px dashed #D1D5DB' }}
      title={(sign.device_name || sign.group_id) ? 'Screen offline' : 'No screen assigned'} />;
  }
  return (
    <img src={src} alt=""
      title={isLive ? 'Live from the screen — click to refresh' : 'Rendered preview'}
      style={{ ...box, borderColor: isLive ? '#34D399' : '#E5E7EB' }}
      onClick={e => { e.stopPropagation(); if (sign.device_name || sign.group_id) onRefresh(); }} />
  );
}

function Empty({ children }) {
  return <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic', padding: '6px 0' }}>{children}</div>;
}
